import type { GanttVisualizationProfileDto } from '../api/models/GanttVisualizationProfileDto';
import type { ResourceDto } from '../api/models/ResourceDto';

type ResourceFilterConfig = {
  type?: string;
  selectedResourceUuids?: string[];
  filterByType?: { resourceTypeUuid?: string };
};

/**
 * Returns a predicate that tells whether a resource is shown under the profile's resource filter.
 * Without a profile or filter, every resource is shown.
 */
export function profileResourcePredicate(
  profile: GanttVisualizationProfileDto | null | undefined
): (resource: Pick<ResourceDto, 'uuid' | 'resourceTypeUuid'>) => boolean {
  const resourceConfig = profile?.resourceConfiguration as { resourceFilter?: ResourceFilterConfig } | undefined;
  const filter = resourceConfig?.resourceFilter;
  const selectedUuids = filter?.selectedResourceUuids ?? [];

  switch (filter?.type) {
    case 'SELECTED':
      // A SELECTED filter without any selection falls back to ALL
      if (selectedUuids.length === 0) return () => true;
      return resource => selectedUuids.includes(resource.uuid ?? '');
    case 'BY_TYPE': {
      const typeUuid = filter.filterByType?.resourceTypeUuid;
      if (!typeUuid) return () => true;
      // Plan resources from the calculated plan carry no type UUID; show them rather than guess
      return resource => resource.resourceTypeUuid == null || resource.resourceTypeUuid === typeUuid;
    }
    case 'BY_ATTRIBUTE':
      // TODO: Implement attribute-based filtering when attribute system is available
      return () => true;
    case 'ALL':
    default:
      return () => true;
  }
}
